import AdminLayout from '@/layouts/AdminLayout.vue'
import PageNotFound from '@/views/PageNotFound.vue'
import {findPerformanceReportPlatform} from '@shared/constants/performanceReportPlatforms'

const DashboardViewComponent = () => import('@/views/DashboardView.vue')

export default {
  path: '/',
  name: 'content',
  component: AdminLayout,

  redirect: {
    name: 'home',
  },

  children: [
    {
      path: 'retainers',
      name: 'retainers',
      component: () => import('@/views/companies/retainers/RetainersPage.vue'),
      meta: {
        title: 'Retainers',
        access: ['hasAccessToRetainers'],
      },
    },
    {
      path: 'creative-studio-main',
      name: 'creative-studio-main',
      meta: {
        title: 'Creative & CRO',
      },
      redirect: {
        name: 'creative-studio-main.our-studio',
      },
      children: [
        {
          path: 'our-studio',
          name: 'creative-studio-main.our-studio',
          component: () => import('@/views/creative-studio-main/our-studio/OurStudio.vue'),
          meta: {
            parent: 'creative-studio-main',
            title: 'Our Studio',
            access: ['hasAccessToCreativeStudio'],
          },
        },
        {
          path: 'services',
          name: 'creative-studio-main.services',
          component: () => import('@/views/creative-studio-main/services/ServicesPage.vue'),
          meta: {
            parent: 'creative-studio-main',
            title: 'Services',
            access: ['hasAccessToCreativeStudio'],
          },
        },
        {
          path: 'orders',
          name: 'creative-studio-main.orders',
          component: () => import('@/views/creative-studio-main/my-orders/MyOrders.vue'),
          meta: {
            parent: 'creative-studio-main',
            title: 'My orders',
            access: ['hasAccessToCreativeStudio'],
          },
        },
      ],
    },
    // Home Page, renamed to Dashboard (CU-869etyxc9) - stays a standalone top-level page.
    {
      path: '/home',
      name: 'home',
      component: () => import('@/views/home-page/HomePageView.vue'),
      meta: {
        title: 'Dashboard',
      },
    },
    {
      path: '/reports',
      name: 'client-reports',
      redirect: {name: 'reports.main'},
      meta: {
        title: 'Reports',
      },
      children: [
        {
          path: '/dashboard/main',
          name: 'reports.main',
          component: DashboardViewComponent,
          meta: {
            parent: 'client-reports',
            title: 'Main',
            tab: 'main',
            access: ['hasAccessToDashboard'],
          },
        },
        {
          path: '/dashboard/spend',
          name: 'reports.spend',
          component: DashboardViewComponent,
          meta: {
            parent: 'client-reports',
            title: 'Spend',
            tab: 'spends',
            access: ['hasAccessToDashboard'],
          },
        },
        {
          path: '/dashboard/financial-activity',
          name: 'reports.financial-activity',
          component: DashboardViewComponent,
          meta: {
            parent: 'client-reports',
            title: 'Financial Activity',
            tab: 'finance_activity',
            access: ['hasAccessToDashboard'],
          },
        },
        {
          path: '/dashboard/performance/:platform',
          name: 'reports.performance',
          component: DashboardViewComponent,
          meta: {
            parent: 'client-reports',
            title: to => {
              const platform = findPerformanceReportPlatform(to.params.platform)
              return platform ? `${platform.label} Performance` : 'Performance Report'
            },
            access: ['hasAccessToPerformanceReport'],
          },
        },
      ],
    },

    {
      path: '/policy',
      name: 'policy',
      component: () => import('@/views/policy/PolicyView.vue'),
      meta: {
        title: 'Ad Policy',
        access: ['hasAccessToPolicyPage'],
      },
    },

    // User Profile
    {
      path: 'user',
      name: 'user',
      redirect: {
        name: 'user.profile',
      },
      children: [
        {
          path: 'profile',
          name: 'user.profile',
          component: () => import('@/views/user-main/UserMain.vue'),
          meta: {
            title: 'My Profile',
            parent: 'user',
          },
        },
        {
          path: 'notifications',
          name: 'user.notifications',
          component: () => import('@/views/user-main/UserMain.vue'),
          meta: {
            title: 'Notifications',
            parent: 'user',
          },
        },
        {
          path: 'security',
          name: 'user.security',
          component: () => import('@/views/user-main/UserMain.vue'),
          meta: {
            title: 'Security',
            parent: 'user',
          },
        },
      ],
    },
    {
      path: 'team',
      name: 'team',
      component: () => import('@/views/users/UsersList.vue'),
      meta: {
        title: 'Team',
        access: ['hasAccessToTeam'],
      },
    },

    // Ad account requests
    {
      name: 'ad-account-requests',
      path: 'ad-account-requests',
      meta: {
        title: 'Requests',
        access: ['hasAccessToRequestsPage'],
      },
      alias: 'meta-ad-account-requests',
      redirect: {
        name: 'ad-account-requests.accounts.list',
      },
      children: [
        {
          path: 'my-requests',
          name: 'my-requests.list',
          component: () => import('@/views/home-page/MyRequestsList.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'All My Requests',
            access: ['hasAccessToMyRequests'],
          },
        },
        {
          path: 'accounts',
          name: 'ad-account-requests.accounts.list',
          component: () => import('@/views/ad-account-requests/accounts/AdAccountRequestsList.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'Ad Account Requests',
            access: ['hasAccessToAdAccountRequests'],
          },
        },
        {
          path: 'rename',
          name: 'ad-account-requests.rename.list',
          component: () => import('@/views/ad-account-requests/rename/AdAccountRenameRequestList.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'Rename Ad Account Requests',
            access: ['hasAccessToAccountRenameRequests'],
          },
        },
        {
          path: 'top-up',
          name: 'ad-account-requests.top-up.list',
          component: () => import('@/views/ad-account-requests/top-up/ManageFundsList.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'Funds Requests',
            access: ['hasAccessToTopUpRequestsPage'],
          },
        },
        {
          path: 'facebook-pages-requests',
          name: 'ad-account-requests.facebook-pages-requests.list',
          component: () => import('@/views/ad-account-requests/facebook-pages/FacebookPageRequestList.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'FB Page Requests',
            access: ['hasAccessToFBPageRequestsPage'],
          },
        },
        {
          path: 'facebook-pages-requests/request/:id?',
          name: 'ad-account-requests.facebook-pages-requests.form',
          component: () => import('@/views/ad-account-requests/facebook-pages/FacebookPageRequest.vue'),
          meta: {
            parent: 'ad-account-requests.facebook-pages-requests.list',
            title: 'Request',
            access: ['hasAccessToFBPageRequestFormPage'],
            type: 'form',
          },
        },
        {
          path: 'whitelist',
          name: 'whitelist',
          component: () => import('@/views/ad-account-requests/white-lists/AdAccountWhiteListRequestsList.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'Whitelist Ad Account Requests',
            access: ['hasAccessToWhiteListRequestsPage'],
          },
        },
        {
          path: 'share-permissions/:hash?',
          name: 'share-permissions',
          component: () => import('@/views/ad-account-requests/share-permissions/SharePermissionsRequests.vue'),
          meta: {
            parent: 'ad-account-requests',
            title: 'Share Access Requests',
            access: ['hasAccessToSharePermissionRequestsPage'],
          },
        },

        // Meta  accounts request
        {
          path: 'meta/:id?',
          name: 'meta-ad-account-request',
          alias: 'meta-ad-account-requests/:id?',
          component: () => import('@/views/ad-account-requests/accounts/meta/MetaAdAccountRequest.vue'),
          meta: {
            title: 'Meta Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Meta',
            type: 'form',
          },
        },
        {
          path: 'google/:id?',
          name: 'google-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/google/GoogleAdAccountRequest.vue'),
          meta: {
            title: 'Google Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Google',
            type: 'form',
          },
        },
        {
          path: 'tiktok/:id?',
          name: 'tiktok-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/tiktok/TiktokAdAccountRequest.vue'),
          meta: {
            title: 'TikTok Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'TikTok',
            type: 'form',
          },
        },
        {
          path: 'smartnews/:id?',
          name: 'smartnews-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/smartnews/SmartnewsAdAccountRequest.vue'),
          meta: {
            title: 'Smartnews Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Smartnews',
            type: 'form',
          },
        },
        {
          path: 'bigo/:id?',
          name: 'bigo-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/bigo/BigoAdAccountRequest.vue'),
          meta: {
            title: 'Bigo Ads Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Bigo',
            type: 'form',
          },
        },
        {
          path: 'bing/:id?',
          name: 'bing-ad-account-request',
          alias: 'bing-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/bing/BingAdAccountRequest.vue'),
          meta: {
            title: 'Microsoft Ads Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Microsoft Ads',
            type: 'form',
          },
        },
        {
          path: 'snapchat/:id?',
          name: 'snapchat-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/snapchat/SnapchatAdAccountRequest.vue'),
          meta: {
            title: 'Snapchat Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Snapchat',
            type: 'form',
          },
        },
        {
          path: 'revcontent/:id?',
          name: 'revcontent-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/revcontent/RevcontentAdAccountRequest.vue'),
          meta: {
            title: 'Revcontent Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Revcontent',
            type: 'form',
          },
        },
        {
          path: 'newsbreak/:id?',
          name: 'newsbreak-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/newsbreak/NewsbreakAdAccountRequest.vue'),
          meta: {
            title: 'NewsBreak Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'NewsBreak',
            type: 'form',
          },
        },
        {
          path: 'taboola/:id?',
          name: 'taboola-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/taboola/TaboolaAdAccountRequest.vue'),
          meta: {
            title: 'Taboola Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Taboola',
            type: 'form',
          },
        },
        {
          path: 'outbrain/:id?',
          name: 'outbrain-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/outbrain/OutbrainAdAccountRequest.vue'),
          meta: {
            title: 'Outbrain Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Outbrain',
            type: 'form',
          },
        },
        {
          path: 'mintegral/:id?',
          name: 'mintegral-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/mintegral/MintegralAdAccountRequest.vue'),
          meta: {
            title: 'Mintegral Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Mintegral',
            type: 'form',
          },
        },
        {
          path: 'pinterest/:id?',
          name: 'pinterest-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/pinterest/PinterestAdAccountRequest.vue'),
          meta: {
            title: 'Pinterest Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Pinterest',
            type: 'form',
          },
        },
        {
          path: 'mediago/:id?',
          name: 'mediago-ad-account-request',
          component: () => import('@/views/ad-account-requests/accounts/mediago/MediagoAdAccountRequest.vue'),
          meta: {
            title: 'MediaGo Ad Account Request',
            parent: 'ad-account-requests.accounts.list',
            platform: 'Mediago',
            type: 'form',
          },
        },
      ],
    },
    {
      path: 'creatives',
      name: 'creatives',
      redirect: {
        name: 'creatives.list',
      },

      children: [
        {
          path: 'list',
          name: 'creatives.list',
          component: () => import('@/views/creatives/AdCreativeRequestsList.vue'),
          meta: {
            title: 'Creative Moderation Requests',
            access: ['hasAccessToCreativeModerationList'],
          },
        },

        {
          path: 'profile/:id?',
          name: 'creatives.profile',
          component: () => import('@/views/creatives/AdCreativeRequest.vue'),
          meta: {
            title: 'New Request',
            type: 'form',
            access: ['hasAccessToCreativeModerationProfile'],
          },
          beforeEnter: to => {
            if (to.params.id) {
              to.meta.title = `Request #${to.params.id}`
            } else {
              to.meta.title = 'New Request'
            }

            to.meta.parent = 'creatives.list'
          },
        },
      ],
    },

    // Assets
    {
      path: 'assets',
      name: 'assets.list',
      redirect: {
        name: 'assets.ad-accounts.list',
      },
      meta: {
        access: ['hasAccessToAssetsPage'],
      },
      children: [
        {
          path: 'ad-accounts',
          name: 'assets.ad-accounts.list',
          alias: 'meta-ad-accounts',
          component: () => import('@/views/assets/ad-accounts/AdAccountsList.vue'),
          meta: {
            parent: 'assets.list',
            title: 'Ad Accounts',
          },
        },
      ],
    },

    // Invoices
    {
      path: 'finance',
      name: 'finance',
      redirect: {
        name: 'proforma-invoices.list',
      },
      meta: {
        access: ['hasAccessToFinancePage'],
      },
      children: [
        {
          path: 'payments/daily-balance',
          name: 'payments.daily_balance',
          component: () => import('@/views/finance/TheFinance.vue'),
          meta: {
            title: 'Daily Balance',
            parent: 'finance',
            access: ['hasAccessToPaymentsDailyBalance'],
          },
        },

        {
          path: 'proforma-invoices',
          name: 'proforma-invoices.list',
          component: () => import('@/views/finance/TheFinance.vue'),
          meta: {
            title: 'Proforma Invoices',
            parent: 'finance',
          },
        },
        {
          path: 'payments',
          name: 'payments.list',
          component: () => import('@/views/finance/TheFinance.vue'),
          meta: {
            title: 'Payments',
            parent: 'finance',
          },
        },
      ],
    },

    // Not Found
    {path: '/:pathMatch(.*)*', name: 'PageNotFound', component: PageNotFound},
  ],
}
